import addDays from 'date-fns/esm/addDays';
import format from 'date-fns/esm/format';
import isSameDay from 'date-fns/esm/isSameDay';
import isWithinInterval from 'date-fns/esm/isWithinInterval';
import orderBy from 'lodash-es/orderBy';
import { createSelector } from 'reselect';
import { IState } from '..';
import { IOption } from '../../components/Select';
import { getDatesFromRange } from '../../utils/dateUtils';
import { getLogisticsUserWarehouses } from '../auth';
import { ILoadState } from '../lookups';
import { getUnallocatedProductDailyTotals } from '../unallocatedProducts/unallocatedProductsSelectors';
import * as i from './logisticsInterfaces';

export const getVisibleShipmentList = (state: IState): i.IShipmentListItem[] =>
  state.logistics.visibleShipmentListItems.map(id => state.logistics.shipmentListItemHash[id]);

export const getShipment = (shipmentId: number) =>
  createSelector(
    (state: IState) => state.logistics.shipmentHash,
    (hash): i.IShipment | undefined => hash[shipmentId]
  );

export const isRangeLoading = (state: IState): boolean =>
  state.logistics.loading.includes(getLogisticsDateRangeString(state));

export const getShipmentContainer = (shipmentContainerId: number) =>
  createSelector(
    (state: IState) => state.logistics.containerHash,
    (hash): i.IShipmentContainer | undefined => hash[shipmentContainerId]
  );

export const getShipmentContainers = (shipmentId: number) =>
  createSelector(
    [getShipment(shipmentId), (state: IState) => state.logistics.containerHash],
    (shipment, containerHash): i.IShipmentContainer[] => {
      if (!shipment) {
        return [];
      }

      return shipment.containerIds.map(x => containerHash[x]).filter(x => x);
    }
  );

export const getShipmentContainersAsOptions = (shipmentId: number) =>
  createSelector(
    [getShipmentContainers(shipmentId), (state: IState) => state.lookups.shipmentContainerTypes.hash],
    (containers, typeHash) =>
      containers.map<IOption>(c => ({
        label: c.name,
        secondaryLabel: `Type: ${typeHash[c.shipmentContainerTypeId]}`,
        value: c.shipmentContainerId
      }))
  );

export const getShipmentContainersWithItems = (shipmentId: number, shouldGroupItems: boolean) =>
  createSelector(
    [getShipmentContainers(shipmentId), (state: IState) => state.logistics.itemHash],
    (containers, itemHash): i.IShipmentContainerWithItems[] => {
      return containers.map<i.IShipmentContainerWithItems>(container => {
        const shipmentItems = processShipmentContainerItems(container.shipmentItemIds, itemHash, shouldGroupItems);
        return {
          name: container.name,
          shipmentContainerId: container.shipmentContainerId,
          shipmentContainerTypeId: container.shipmentContainerTypeId,
          shipmentId: container.shipmentId,
          shipmentItems
        };
      });
    }
  );

export const getUserFilteredMinLoads = createSelector(
  [(state: IState) => state.logistics.minLoads, getLogisticsUserWarehouses],
  (minLoadHash, warehouses) =>
    Object.values(minLoadHash).filter(
      load =>
        load.shipments.length === 0 ||
        load.shipments.some(
          x => warehouses.includes(x.originWarehouseId) || warehouses.includes(x.destinationWarehouseId)
        )
    )
);

export const getLoadCalendarItems = createSelector(
  [
    getUserFilteredMinLoads,
    (state: IState) => state.logistics.calendarFilters,
    (_: IState, dates: { start: Date; end: Date }) => dates
  ],
  (minLoads, filters, dates) => {
    const start = addDays(dates.start, -1);
    const end = addDays(dates.end, 1);

    return minLoads.reduce<i.ILoadCalendarItem[]>((arr, item) => {
      if (
        isWithinInterval(new Date(item.departureDate), { start, end }) &&
        (!filters.company || item.shipments.some(y => filters.company === y.companyId)) &&
        (filters.warehouses.length === 0 ||
          item.shipments.some(
            y =>
              filters.warehouses.includes(y.originWarehouseId) || filters.warehouses.includes(y.destinationWarehouseId)
          ))
      ) {
        const calItem = convertMinLoadToLogisticsCalendarItem(item);
        arr.push(calItem);
      }

      return arr;
    }, []);
  }
);

export const getLoadCalendarItemsByDate = (date: Date) =>
  createSelector(getUserFilteredMinLoads, minLoads =>
    minLoads.reduce<i.ILoadCalendarItem[]>((arr, item) => {
      if (isSameDay(new Date(item.departureDate), date)) {
        const calItem = convertMinLoadToLogisticsCalendarItem(item);
        arr.push(calItem);
      }

      return arr;
    }, [])
  );

export const getLoadCalendarItemsByDateAsOptions = (date: Date, includeLocked: boolean = false) =>
  createSelector(
    [
      getLoadCalendarItemsByDate(date),
      (state: IState) => state.lookups.loadTypes.hash,
      (state: IState) => state.lookups.loadStates.hash
    ],
    (items, typeHash, stateHash) =>
      items
        .filter(x => includeLocked || !stateHash[x.loadStateId].changeLocked)
        .map<IOption>(x => ({
          label: `Load ${x.loadNumber} - ${typeHash[x.loadTypeId]}`,
          secondaryLabel: x.shipments.map(y => y.location).join(', '),
          value: x.loadNumber
        }))
  );

const convertMinLoadToLogisticsCalendarItem = (load: i.ILoadMin): i.ILoadCalendarItem => ({
  ...load,
  date: new Date(load.departureDate),
  title: `Load ${load.loadNumber}${
    load.shipments.length > 0
      ? ` - ${orderBy(load.shipments, x => x.stopNumber)
          .map(x => x.location)
          .join(' → ')}`
      : ''
  }`
});

export const getLogisticsDateRangeString = ({
  filters: {
    salesShipments: { dateRange }
  }
}: IState): string => `${format(dateRange.start, 'MM-dd-yyyy')}_${format(dateRange.end, 'MM-dd-yyyy')}`;

export const getCurrentLoad = createSelector(
  [
    (state: IState) => state.logistics.loads,
    (state: IState) => state.logistics.shipmentHash,
    (_: any, loadNumber: string) => loadNumber
  ],
  (loadHash, shipmentHash, loadNumber): i.ILoad | undefined => {
    const load = loadHash[loadNumber];

    if (!load) {
      return undefined;
    }

    return {
      ...load,
      shipments: load.shipmentIds.map<i.IShipment>(x => shipmentHash[x])
    };
  }
);

export const getLoad = createSelector(
  [getCurrentLoad, (state: IState) => state.logistics.optimisticLoadUpdates],
  (load, optimisticUpdates): i.ILoad | undefined => {
    if (!load) {
      return undefined;
    }

    const updates = optimisticUpdates[load.loadNumber] ?? {};

    return { ...load, ...updates };
  }
);

export const getCarrierLoads = createSelector(
  [(state: IState) => state.logistics.minLoads, (_: IState, carrierId: string) => carrierId],
  (minLoads, carrierId): i.ILoadMin[] =>
    orderBy(
      Object.values(minLoads).filter(x => x.carrierId === carrierId),
      (x: i.ILoadMin) => new Date(x.departureDate),
      'desc'
    )
);

export const getLoadStateChangeLocked = (loadStateId: number) =>
  createSelector(getLoadState(loadStateId), loadState => (loadState ? loadState.changeLocked : false));

export const getLoadState = (loadStateId: number) =>
  createSelector(
    (state: IState) => state.lookups.loadStates.hash,
    (hash): ILoadState | undefined => hash[loadStateId]
  );

export const getLoadStateTitle = (
  loadStateId: number,
  type: keyof Pick<ILoadState, 'internalTitle' | 'externalTitle'> = 'internalTitle'
) => createSelector(getLoadState(loadStateId), state => (state ? state[type] : 'Unknown'));

export const getNextLoadState = (loadStateId: number) =>
  createSelector([(state: IState) => state.lookups.loadStates, getLoadState(loadStateId)], ({ hash, ids }, current) => {
    return current
      ? orderBy(
          ids.map(x => hash[x]),
          x => x.order
        ).filter(x => x.order > current.order && !x.removed)[0]
      : undefined;
  });

const getDailySummariesArray = createSelector(
  (state: IState) => state.logistics.dailySummaryHash,
  hash => Object.values(hash)
);

const getAuthorizedDailySummaryHash = createSelector(
  [(state: IState) => state.logistics.dashboardFilters, getDailySummariesArray, getLogisticsUserWarehouses],
  (filters, summaries, warehouses): Record<number, i.ILogisticsDailySummary> => {
    return summaries.reduce<Record<number, i.ILogisticsDailySummary>>((hash, item) => {
      const authed = item.values.filter(
        x =>
          warehouses.includes(x.warehouseId) &&
          // if there are no selected filters or the filters include the warehouse
          (filters.warehouses.length === 0 || filters.warehouses.includes(x.warehouseId)) &&
          (!filters.company || x.companyId === filters.company)
      );

      hash[item.departureDate] = { departureDate: item.departureDate, values: authed };

      return hash;
    }, {});
  }
);

export const getLogisticsDailySummaryAggregate = createSelector(
  [getAuthorizedDailySummaryHash, getUnallocatedProductDailyTotals, (_: IState, range: Interval) => range],
  (summaries, unallocated, range): i.ILogisticsDailySummaryAggregate[] => {
    return getDatesFromRange(range).map<i.ILogisticsDailySummaryAggregate>(d => {
      const dateNum = d.getTime();
      const summary: { shipped: number; scheduled: number } = summaries[dateNum]
        ? summaries[dateNum].values.reduce<{ shipped: number; scheduled: number }>(
            (obj, item) => ({ shipped: obj.shipped + item.shipped, scheduled: obj.scheduled + item.scheduled }),
            { scheduled: 0, shipped: 0 }
          )
        : { scheduled: 0, shipped: 0 };
      return {
        departureDate: dateNum,
        scheduled: summary.scheduled,
        shipped: summary.shipped,
        unallocated: unallocated[dateNum]
      };
    });
  }
);

export const getCarriers = createSelector(
  (state: IState) => state.logistics.carriers,
  carrierHash => orderBy(Object.values(carrierHash), x => x.name)
);

export const getLoadRoute = createSelector(
  [(state: IState) => state.logistics.routes, (_: unknown, loadNumber: string) => loadNumber],
  (hash, loadNumber) => (hash[loadNumber] ? hash[loadNumber] : undefined)
);

export const getLoadEvents = (loadNumber: string, direction = 'desc') =>
  createSelector(
    (state: IState) => state.logistics.events,
    eventHash => (eventHash[loadNumber] ? orderBy(eventHash[loadNumber], x => new Date(x.time), 'desc') : [])
  );

const processShipmentContainerItems = (
  shipmentItemIds: number[],
  itemHash: Record<string, i.IShipmentItem>,
  shouldGroupItems: boolean
): i.IShipmentItem[] =>
  Object.values(
    shipmentItemIds.reduce<Record<string, i.IShipmentItem>>((hash, itemId) => {
      // destructured because it was mutating way the hash
      const item = { ...itemHash[itemId] };
      if (shouldGroupItems) {
        const key = `${item.itemId}_${item.inventDimId}`;
        if (hash[key]) {
          hash[key].quantity += item.quantity;
          hash[key].lineAmount += item.lineAmount;
        } else {
          hash[key] = item;
        }
      } else {
        hash[item.shipmentItemId] = item;
      }
      return hash;
    }, {})
  );
